/**
 * Vessel detail enricher: real AIS identity + navigation (name, MMSI, decoded
 * flag, ship type, speed, course, heading). Ship type comes from the track's
 * decoded category; the flag is decoded deterministically from the MMSI MID.
 * No fabricated fields — a vessel photo would need a keyed source (follow-up).
 */

import type { Track } from '@vtak/shared';
import type { MarkerData } from '@/map/markers/marker-state';
import { type EntityDetail, type DetailRow, formatCoords, textRow } from './entity-detail';
import { flagForMmsi } from './mmsi-flag';

/** Layer id for live vessel (AIS) tracks. */
export const VESSEL_LAYER = 'maritime:vessels';

/** Build the vessel detail view from real AIS props + the decoded category. */
export function enrichVessel(data: MarkerData, track: Track | undefined): EntityDetail {
  const props = track?.props ?? {};
  const mmsi = numberProp(props, 'mmsi');
  const speed = numberProp(props, 'speed');
  const course = numberProp(props, 'course');
  const heading = numberProp(props, 'heading');

  return {
    sections: [
      {
        title: 'Identity',
        rows: [
          textRow('Name', data.callsign),
          textRow('MMSI', mmsi || ''),
          textRow('Flag', flagForMmsi(mmsi)),
          textRow('Type', titleCase(track?.category)),
        ],
      },
      {
        title: 'Navigation',
        rows: [
          { label: 'Speed', value: `${speed.toFixed(1)} kn` },
          { label: 'Course', value: `${Math.round(course)}°` },
          degreeRow('Heading', heading),
          { label: 'Position', value: formatCoords(data.position) },
        ],
      },
    ],
    followUps: ['Vessel photo + registry details require a keyed marine source (e.g. AIS registry) — follow-up.'],
  };
}

/** Read a numeric track prop, defaulting to 0. */
function numberProp(props: Record<string, unknown>, key: string): number {
  const value = props[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

/** A degree row, blank when the heading is unavailable (AIS often omits it). */
function degreeRow(label: string, value: number): DetailRow {
  return { label, value: value > 0 ? `${Math.round(value)}°` : '' };
}

/** Capitalize the first letter of a category code, or blank. */
function titleCase(value: string | undefined): string {
  if (!value) return '';
  return value.charAt(0).toUpperCase() + value.slice(1);
}
